/**
 * Organizations service that abstracts organization operations from Firebase to FastAPI.
 */

import apiClient, { ApiResponse } from './client';

export interface Organization {
  id: string;
  name: string;
  client_key: string;
  logo_url?: string;
  support_email?: string;
  platform_resources?: {
    help_center?: Resource;
    product_release_notes?: Resource;
    api_documentation?: Resource;
    status_page?: Resource;
  };
  hyperspell_api_key?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Resource {
  url: string;
  name: string;
}

export interface OrganizationMember {
  id: string;
  userId: string;
  organization_id: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  email: string;
  displayName?: string;
  photoURL?: string;
}

export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  email: string;
  role: 'admin' | 'member';
  token: string;
  created_at: string;
  expires_at: string;
  created_by?: string;
}

export interface CreateOrganizationRequest {
  name: string;
  companyUrl?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  support_email?: string;
  platform_resources?: Organization['platform_resources'];
}

export interface CreateInvitationRequest {
  email: string;
  role: 'admin' | 'member';
}

export interface AcceptInvitationRequest {
  token: string;
}

export interface AcceptInvitationByTokenRequest {
  token: string;
  email: string;
}

export interface AddMemberRequest {
  email: string;
  role: 'admin' | 'member';
}

export interface UpdateMemberRoleRequest {
  role: 'admin' | 'member';
}

export interface CompanyUpdateRequest {
  company_data: {
    company_name?: string;
    help_center_url?: string;
    summary?: string;
    product?: string;
    tone_guide?: string;
    key_features?: Array<string>
  }
}

export interface CompanyContextGenerateRequest {
  url: string;
}

export interface InvitationValidationResponse {
  valid: boolean;
  organization_name?: string;
  inviter_email?: string;
  role?: string;
  error?: string;
}

export class OrganizationsService {
  /**
   * Create a new organization
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<ApiResponse<Organization>> {
    return apiClient.post<Organization>('/api/organizations/', data);
  }

  /**
   * Get organizations for current user
   */
  async getUserOrganizations(): Promise<ApiResponse<{ organizations: Organization[] }>> {
    return apiClient.get<{ organizations: Organization[] }>('/api/organizations/');
  }

  /**
   * Get organization by ID
   */
  async getOrganization(organizationId: string): Promise<ApiResponse<Organization>> {
    return apiClient.get<Organization>(`/api/organizations/${organizationId}`);
  }

  /**
   * Update organization
   */
  async updateOrganization(organizationId: string, data: UpdateOrganizationRequest): Promise<ApiResponse<Organization>> {
    return apiClient.put<Organization>(`/api/organizations/${organizationId}`, data);
  }

  /**
   * Delete organization
   */
  async deleteOrganization(organizationId: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>(`/api/organizations/${organizationId}`);
  }

  /**
   * Get organization members
   */
  async getOrganizationMembers(organizationId: string): Promise<ApiResponse<{ members: OrganizationMember[] }>> {
    return apiClient.get<{ members: OrganizationMember[] }>(`/api/organizations/${organizationId}/members`);
  }

  /**
   * Add member to organization
   */
  async addMember(organizationId: string, data: AddMemberRequest): Promise<ApiResponse<OrganizationMember>> {
    return apiClient.post<OrganizationMember>(`/api/organizations/${organizationId}/members`, data);
  }

  /**
   * Update member role
   */
  async updateMemberRole(organizationId: string, memberUserId: string, data: UpdateMemberRoleRequest): Promise<ApiResponse<OrganizationMember>> {
    return apiClient.put<OrganizationMember>(`/api/organizations/${organizationId}/members/${memberUserId}/role`, data);
  }

  /**
   * Remove member from organization
   */
  async removeMember(organizationId: string, memberUserId: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>(`/api/organizations/${organizationId}/members/${memberUserId}`);
  }

  /**
   * Create invitation
   */
  async createInvitation(organizationId: string, data: CreateInvitationRequest): Promise<ApiResponse<OrganizationInvitation>> {
    return apiClient.post<OrganizationInvitation>(`/api/organizations/${organizationId}/invitations`, data);
  }

  /**
   * Get organization invitations
   */
  async getInvitations(organizationId: string): Promise<ApiResponse<{ invitations: OrganizationInvitation[] }>> {
    return apiClient.get<{ invitations: OrganizationInvitation[] }>(`/api/organizations/${organizationId}/invitations`);
  }

  /**
   * Accept invitation
   */
  async acceptInvitation(organizationId: string, data: AcceptInvitationRequest): Promise<ApiResponse<OrganizationMember>> {
    return apiClient.post<OrganizationMember>(`/api/organizations/${organizationId}/invitations/accept`, data);
  }

  /**
   * Accept invitation by token (public endpoint)
   */
  async acceptInvitationByToken(data: AcceptInvitationByTokenRequest): Promise<ApiResponse<OrganizationMember>> {
    return apiClient.post<OrganizationMember>('/api/organizations/invitations/accept-by-token', data);
  }

  /**
   * Revoke invitation
   */
  async revokeInvitation(organizationId: string, email: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>(`/api/organizations/${organizationId}/invitations?email=${encodeURIComponent(email)}`);
  }

  /**
   * Validate invitation (public endpoint)
   */
  async validateInvitation(token: string, email: string): Promise<ApiResponse<InvitationValidationResponse>> {
    return apiClient.get<InvitationValidationResponse>(`/api/organizations/invitations/validate?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`);
  }

  /**
   * Get pending invitations by email (public endpoint)
   */
  async getPendingInvitationsByEmail(email: string): Promise<ApiResponse<{ invitations: OrganizationInvitation[] }>> {
    return apiClient.get<{ invitations: OrganizationInvitation[] }>(`/api/organizations/invitations/by-email?email=${encodeURIComponent(email)}`);
  }

  /**
   * Upload organization logo
   */
  async uploadLogo(organizationId: string, file: File): Promise<ApiResponse<{ message: string; logo_url: string }>> {
    return apiClient.uploadFile<{ message: string; logo_url: string }>(`/api/organizations/${organizationId}/upload-logo`, file);
  }

  /**
   * Update company data
   */
  async updateCompanyData(organizationId: string, data: CompanyUpdateRequest): Promise<ApiResponse<{ message: string }>> {
    return apiClient.put<{ message: string }>(`/api/organizations/${organizationId}/company`, data);
  }

  /**
   * Generate company context
   */
  async generateCompanyContext(organizationId: string, data: CompanyContextGenerateRequest): Promise<ApiResponse<{ context: string }>> {
    return apiClient.post<{ context: string }>(`/api/organizations/${organizationId}/company/generate-context`, data);
  }

  /**
   * Accept invitation via Supabase authentication
   */
  async acceptInvitationSupabase(email: string, orgId: string, role: string, accessToken: string): Promise<ApiResponse<Organization>> {
    return apiClient.post<Organization>(`/api/organizations/invitations/accept-supabase?email=${encodeURIComponent(email)}&org_id=${encodeURIComponent(orgId)}&role=${encodeURIComponent(role)}&access_token=${encodeURIComponent(accessToken)}`);
  }
}

// Create and export the default organizations service instance
const organizationsService = new OrganizationsService();

export default organizationsService;
